<template>
  <div class="alert alert-secondary border mb-3">
    <div class="alert-heading mb-4">
      <h5 class="float-end mb-0">
        <span class="badge text-bg-primary" :class="'text-bg-' + stateColor">
          {{ $t(`js.backend.campaign_controls.state.${currentState}`) }}
        </span>
      </h5>
      <h4 class="mb-0">{{ $t(`js.backend.campaign_controls.state_header.${currentState}`) }}</h4>
    </div>
    <div v-show="currentState === 'ready'">
      <template v-if="!campaign.template_id">
        <div class='alert alert-warning p-3 text-center mt-3'>{{ $t('backend.campaigns.show.no_template_selected') }}</div>
      </template>
      <div v-else class="row">
        <div class="d-flex flex-column justify-content-between text-center col-6 border-end">
          <b class="mb-2">{{ $t("js.backend.campaign_controls.scheduled_send_out") }}</b>
          <input class="form-control mb-3" type="datetime-local" id="campaign_schedule_at" name="campaign[schedule_at]"
            :min="minDate" v-model="scheduleAt">
          <div v-if="errors.length > 0" class="bg-danger p-2 mb-3">
            <p class="mb-0 text-white" v-for="error in errors">{{ error }}</p>
          </div>
          <button type="button" class="btn btn-primary" @click="schedule">
            {{ $t("js.backend.campaign_controls.send_on_schedule") }}
          </button>
        </div>

        <div class="d-flex flex-column justify-content-between text-center col-6">
          <b>{{ $t("js.backend.campaign_controls.immediate_send_out") }}</b>
          <button type="button" class="btn btn-primary" @click="deliver">
            {{ $t("js.backend.campaign_controls.send_out_now") }}
          </button>
        </div>
      </div>
    </div>
    <div class="mb-3">
      <div v-if="currentState === 'scheduled'" class="text-center">
        <p v-html="$t(`js.backend.campaign_controls.state_body.${currentState}`,
             {recipientCount: campaign.total_count, scheduledAt: formattedScheduledAt})"></p>
        <button type="button" class="btn btn-danger" @click.prevent="deschedule">{{ $t('js.backend.campaign_controls.cancel_schedule') }}</button>
      </div>
      <div v-if="currentState === 'queued'">
        <p v-html="$t(`js.backend.campaign_controls.state_body.${currentState}`)"></p>
        <button type="button" class="btn btn-danger" @click.prevent="abort">{{ $t('js.backend.campaign_controls.abort') }}</button>
      </div>
      <div v-if="currentState === 'sending'">
        <Progress :count="campaign.sent_count" :total="campaign.total_count" class="mb-3"/>
        <button type="button" class="btn btn-danger" @click.prevent="abort">{{ $t('js.backend.campaign_controls.abort') }}</button>
      </div>
      <div v-if="currentState === 'stopped'">
        <p v-html="$t(`js.backend.campaign_controls.state_body.${currentState}`, {sentCount: campaign.sent_count || 0})"></p>
      </div>
      <div v-if="currentState === 'sent'">
        <p v-if="campaign.schedule_at"
           v-html="$t('js.backend.campaign_controls.state_body.sent.scheduled', {scheduledAt: formattedScheduledAt})">

        </p>
        <p v-html="$t('js.backend.campaign_controls.state_body.sent.finished', {sentAt: formattedSentAt, sentCount: campaign.sent_count})">

        </p>
      </div>
      <div v-if="currentState === 'failed'">
        <div class="alert alert-danger">
          <div class="h5 alert-heading" v-text="$t('js.backend.campaign_controls.an_unexpected_error_occurred')" />
          <p v-text="$t('js.backend.campaign_controls.an_unexpected_error_occurred_body')" />
          <button type="button" class="w-100 btn btn-danger" @click.prevent="manualRetry">{{ $t('js.backend.campaign_controls.retry') }}</button>
        </div>
      </div>
    </div>
    <div class="hstack gap-2 flex-wrap" v-if="currentState === 'sending' || currentState === 'stopped' || currentState === 'failed' || currentState === 'sent'">
      <template v-for="{status, icon, color} in [
        { status: 'pending', icon: 'clock', color: 'primary'},
        { status: 'completed', icon: 'check', color: 'success'},
        { status: 'failed', icon: 'xmark', color: 'danger'},
        { status: 'skipped', icon: 'forward-step', color: 'warning'},
           ]">
        <a class="badge text-decoration-hover" :class="'text-bg-' + color" :href="sendoutLinks[status]">
          <i class="fas me-2" :class="'fa-' + icon"></i>
          {{ $t(`js.backend.campaign_controls.sendout_counts.${status}`, { count: sendoutCounts[status] }) }}
        </a>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import apiClient from "@/entrypoints/frontend/apiClient";
import Progress from "../shared/Progress.vue";
import { CampaignChannelSubscription } from "@/channels/CampaignChannel";
// import { useI18n } from 'vue-i18n';
import i18n from '@/entrypoints/shared/i18n_backend';

// const i18n = useI18n();

enum SendoutStatus {
  pending = "pending",
  skipped = "skipped",
  failed = "failed",
  completed = "completed",
}

enum CampaignState {
  ready = "ready",
  stopped = "stopped",
  failed = "failed",
  sending = "sending",
  sent = "sent",
  queued = "queued",
  scheduled = "scheduled",
}

interface Campaign {
  id: number,
  workflow_state: CampaignState,
  template_id: number,
  total_count: number,
  sent_count: number,
  sent_at: string,
  schedule_at: string,
  sendout_counts: Record<SendoutStatus, number>,
}

type SendoutLinks = Record<SendoutStatus, string>;

// Since parsing a date will take time zone into account, but printing a date will not,
// then we simply ignore the time zone info.
// This is okay, since rails will return date times in a configured time zone.
// Fx. 2020-11-30T10:45:55.024+01:00 will become 2020-11-30T10:45:55.024 which
// formats nicely without any offset
function parseWoTz(dateString: string) {
  return Date.parse(dateString.split("+")[0]);
}

const props = defineProps<{
  initialCampaign: Campaign,
  sendoutLinks: SendoutLinks,
}>();

const scheduleAt = ref<string>(null);
const errors = ref<string[]>([]);
const campaign = ref<Campaign>(props.initialCampaign);
const campaignChannelSubscription = ref<CampaignChannelSubscription>(null);

const currentState = computed(()=> campaign.value.workflow_state);
const sendoutCounts = computed(()=> Object.assign({ pending: 0, skipped: 0, failed: 0, completed: 0 }, campaign.value.sendout_counts));
const stateColor = computed(()=> {
  switch (currentState.value) {
    case "ready":
      return "success";
    case "stopped":
    case "failed":
      return "danger";
    default:
      return "primary";
  }
});
const minDate = computed(()=> {
  const d = new Date();
  const dmonth = d.getMonth() + 1;
  const month = dmonth < 10 ? "0" + (dmonth) : dmonth;
  const time = d.getFullYear() + "-" + month + "-" + d.getDate() + "T00:00";
  return time;
});
const formattedScheduledAt = computed(()=> i18n.global.d(parseWoTz(campaign.value.schedule_at), "long"));
const formattedSentAt = computed(()=> i18n.global.d(parseWoTz(campaign.value.sent_at), "long"));

async function deliver() {
  await apiClient.patch(campaign.value.id + "/deliver");
}
async function schedule() {
  await apiClient.patch(campaign.value.id + "/schedule", { schedule_at: scheduleAt.value })
    .then(res => {
      if (res.data.errors) {
        errors.value = res.data.errors;
      }
    });
}
async function deschedule() {
  await apiClient.patch(campaign.value.id + "/deschedule")
    .then(res => {
      if (res.status >= 200 && res.status < 300) {
        errors.value = [];
      }
    });
}
async function abort() {
  await apiClient.patch(campaign.value.id + "/abort");
}
async function manualRetry() {
  await apiClient.patch(campaign.value.id + "/retry");
}

onMounted(() => {
  campaignChannelSubscription.value = new CampaignChannelSubscription(campaign.value.id, {
    received: (data: any) => {
      campaign.value = Object.assign(campaign.value, data);
    },
  });
  scheduleAt.value = campaign.value.schedule_at;
});

onBeforeUnmount(() => {
  campaignChannelSubscription.value?.unsubscribe();
});
</script>
